import { SortType } from '../types/sort.type';
import { SortDirection } from '../types/sort-direction.type';
import { SortPropDir } from '../types/sort-prop-dir.type';
import { TableColumn } from '../types/table-column.type';
import { getterForProp } from './column-prop-getters';
/**
 * Gets the next sort direction
 */
export function nextSortDir(sortType: SortType, current: SortDirection): SortDirection | undefined {
  if (sortType === SortType.single) {
    if (current === SortDirection.ASC) {
      return SortDirection.DESC;
    } else {
      return SortDirection.ASC;
    }
  } else {
    if (!current) {
      return SortDirection.ASC;
    } else if (current === SortDirection.ASC) {
      return SortDirection.DESC;
    } else if (current === SortDirection.DESC) {
      return undefined;
    }
    // avoid TS7030: Not all code paths return a value.
    return undefined;
  }
}

/**
 * Adapted from fueld-ui on 6/216
 * https://github.com/FuelInteractive/fuel-ui/tree/master/src/pipes/OrderBy
 */
export function orderByComparator(a: unknown, b: unknown): number {
  let aVal = a;
  let bVal = b;
  if (aVal === null || typeof aVal === 'undefined') {aVal = 0;}
  if (bVal === null || typeof bVal === 'undefined') {bVal = 0;}
  if (aVal instanceof Date && bVal instanceof Date) {
    if (aVal < bVal) {return -1;}
    if (aVal > bVal) {return 1;}
  } else if (isNaN(parseFloat(aVal as string)) || !isFinite(aVal as number) || isNaN(parseFloat(bVal as string)) || !isFinite(bVal as number)) {
    // Convert to string in case of a=0 or b=0
    const aStr = String(aVal);
    const bStr = String(bVal);
    // Isn't a number so lowercase the string to properly compare
    if (aStr.toLowerCase() < bStr.toLowerCase()) {return -1;}
    if (aStr.toLowerCase() > bStr.toLowerCase()) {return 1;}
  } else {
    // Parse strings as numbers to compare properly
    if (parseFloat(aVal as string) < parseFloat(bVal as string)) {return -1;}
    if (parseFloat(aVal as string) > parseFloat(bVal as string)) {return 1;}
  }

  // equal each other
  return 0;
}

/**
 * creates a shallow copy of the `rows` input and returns the sorted copy. this function
 * does not sort the `rows` argument in place
 */
export function sortRows(rows: Array<unknown>, columns: Array<TableColumn>, dirs: Array<SortPropDir>): Array<unknown> {
  if (!rows) {return [];}
  if (!dirs || !dirs.length || !columns) {return [...rows];}

  /**
   * record the row ordering of results from prior sort operations (if applicable)
   * this is necessary to guarantee stable sorting behavior
   */
  const rowToIndexMap = new Map<unknown, number>();
  rows.forEach((row: unknown, index: number) => rowToIndexMap.set(row, index));

  const temp = [...rows];
  const cols = columns.reduce((obj: Record<string, (...args: Array<unknown>) => number>, col: TableColumn) => {
    if (col.comparator && typeof col.comparator === 'function') {
      obj[col.prop as string] = col.comparator;
    }
    return obj;
  }, {});

  // cache valueGetter and compareFn so that they
  // do not need to be looked-up in the sort function body
  const cachedDirs = dirs.map((dir: SortPropDir) => {
    const prop = dir.prop;
    return {
      prop,
      dir: dir.dir,
      valueGetter: getterForProp(prop),
      compareFn: (cols)[prop as string] || orderByComparator
    };
  });

  return temp.sort(function(rowA: unknown, rowB: unknown) {
    for (const cachedDir of cachedDirs) {
      // Get property and value getters for column to be sorted
      const { prop, valueGetter }: { prop: string | number; valueGetter: (row: unknown, prop: string | number) => unknown } = cachedDir;
      // Get A and B cell values from rows based on properties of the columns
      const propA = valueGetter(rowA, prop);
      const propB = valueGetter(rowB, prop);

      // Compare function gets five parameters:
      // Two cell values to be compared as propA and propB
      // Two rows corresponding to the cells as rowA and rowB
      // Direction of the sort for this column as SortDirection
      // Compare can be a standard JS comparison function (a,b) => -1|0|1
      // as additional parameters are silently ignored. The whole row and sort
      // direction enable more complex sort logic.
      const comparison =
        cachedDir.dir !== SortDirection.DESC
          ? cachedDir.compareFn(propA, propB, rowA, rowB, cachedDir.dir)
          : -cachedDir.compareFn(propA, propB, rowA, rowB, cachedDir.dir);

      // Don't return 0 yet in case of needing to sort by next property
      if (comparison !== 0) {return comparison;}
    }

    if (!(rowToIndexMap.has(rowA) && rowToIndexMap.has(rowB))) {return 0;}

    /**
     * all else being equal, preserve original order of the rows (stable sort)
     */
    return rowToIndexMap.get(rowA) < rowToIndexMap.get(rowB) ? -1 : 1;
  });
}
